import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ClientBaseResponse, ClientReviewResponse, WidgetCommonLayoutConfiguration } from '@vestico/model';

import { useLowPowerMode } from '@vestico/shared';

export interface InViewVideoProps {
  layout: WidgetCommonLayoutConfiguration;
  responses: ClientReviewResponse[];
  targetIndex?: number;
}

export interface InViewVideoResp {
  firstInViewVideoResponse: ClientBaseResponse | undefined;
  updateInView: (index: number, inView: boolean) => void;
}

export const useInViewVideo = ({ layout, responses, targetIndex }: InViewVideoProps) => {
  const autoPlayVideoEnabled = useMemo(() => !!layout.autoplayVideo, [layout.autoplayVideo]);

  const inViewVideoResponsesRef = useRef<number[]>([]);
  const inViewResponsesRef = useRef<number[]>([]);
  const [firstInViewVideoResponseIndex, setFirstInViewVideoResponseIndex] = useState<number | undefined>(undefined);
  const isLowPowerMode = useLowPowerMode();

  useLayoutEffect(() => {
    setFirstInViewVideoResponseIndex(undefined);
  }, [responses]);

  useLayoutEffect(() => {
    if (isLowPowerMode) {
      setFirstInViewVideoResponseIndex(undefined);
    }
  }, [isLowPowerMode]);

  const updateResponseInViewIndexes = useCallback((responses: number[], index: number, inView: boolean) => {
    let indexInArray = responses.length;
    let containsIndex = false;

    let currentArrayIndex = 0;
    for (const currentResponseIndex of responses) {
      if (currentResponseIndex >= index) {
        indexInArray = currentArrayIndex;
        containsIndex = currentResponseIndex === index;
        break;
      }
      currentArrayIndex++;
    }

    const insertionIndex = indexInArray;
    if (inView && !containsIndex) {
      responses.splice(insertionIndex, 0, index);
    } else if (!inView && containsIndex) {
      responses.splice(insertionIndex, 1);
    }
  }, []);

  const updateInView = useCallback(
    (index: number, inView: boolean) => {
      const response = responses[index];

      if (!autoPlayVideoEnabled || !response || isLowPowerMode) {
        return;
      }

      // update all responses first
      updateResponseInViewIndexes(inViewResponsesRef.current, index, inView);

      if (response.media.videoPreviews) {
        // update in video view responses
        updateResponseInViewIndexes(inViewVideoResponsesRef.current, index, inView);
      }

      const { current: inViewResponses } = inViewResponsesRef;
      const { current: inViewVideoResponses } = inViewVideoResponsesRef;

      // find the first index that is video playable and equal or less to our target index
      let firstVideoIndex: number | undefined;

      for (const overallResponseIndex of inViewVideoResponses) {
        firstVideoIndex = overallResponseIndex;
        const videoIndexInAllResponses = inViewResponses.indexOf(overallResponseIndex);

        if (!targetIndex || videoIndexInAllResponses === -1 || videoIndexInAllResponses >= targetIndex) {
          break;
        }
      }

      if (firstVideoIndex !== undefined) {
        setFirstInViewVideoResponseIndex(firstVideoIndex);
      }
    },
    [autoPlayVideoEnabled, isLowPowerMode, responses, targetIndex, updateResponseInViewIndexes]
  );

  const response: InViewVideoResp = useMemo(() => {
    return {
      firstInViewVideoResponse:
        autoPlayVideoEnabled && firstInViewVideoResponseIndex !== undefined ? responses[firstInViewVideoResponseIndex] : undefined,
      updateInView,
    };
  }, [autoPlayVideoEnabled, firstInViewVideoResponseIndex, responses, updateInView]);

  return response;
};
